import clsx from 'clsx'
import React, {useCallback, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {FormError, InputTextFormik, SelectFormik} from '../../../../../_gori/partials/widgets'
import {INSURANCE_TYPE, OrderStatus} from '../../core/_const'
import {convertCurrency, toAbsoluteUrl} from '../../../../../_gori/helpers'
import {SeeDetailModal} from '../../modals/order-child/SeeDetailModal'
import {InputCheckboxFormik} from '../../../../../_gori/partials/widgets/form/InputCheckboxFormik'
import Cookies from 'js-cookie/dist/js.cookie.mjs'
import {isNull, sortBy} from 'lodash'
import {ACCEPT_TERM_UPIC_KEY} from '../../../../../_gori/constants'

type Props = {
  formik: any
  disabled: boolean
  setIsAccept: any
  isAccept: boolean
  status?: string
  isModal?: boolean
  loadingRate?: boolean
  isErrorUpicAccept: boolean
  setIsErrorUpicAccept: (value: boolean) => void
}

export const InsuranceSignature: React.FC<Props> = ({
  formik,
  disabled = false,
  setIsAccept,
  isAccept,
  status,
  isModal = false,
  loadingRate = false,
  isErrorUpicAccept = false,
  setIsErrorUpicAccept,
}) => {
  const intl = useIntl()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const addOnServices = statesGlobal.addOnServices
  const [showSeeDetail, setShowSeeDetail] = useState(false)
  const optionsSignature = useMemo(() => {
    return [
      {label: intl.formatMessage({id: 'NONE'}), value: null},
      ...(addOnServices?.signatures || []),
    ]
  }, [addOnServices?.signatures, intl])

  const isBaeGuardInsurance = useMemo(
    () => formik.values?.insurance_option?.provider === INSURANCE_TYPE.BAE_GUARD,
    [formik.values?.insurance_option?.provider]
  )

  const BAEGUARD_RATE_PER_DVU_DOMESTIC = 0.6
  const BAEGUARD_RATE_PER_DVU_INTERNATIONAL = 1.1

  const estimatedInsuranceFee = useMemo(() => {
    if (!isBaeGuardInsurance) return null
    const declaredValue = Number(formik.values?.insurance_option?.insured_amount) || 0
    if (declaredValue <= 0) return null
    const isInternational = formik.values?.international ?? false
    const ratePerDvu = isInternational
      ? BAEGUARD_RATE_PER_DVU_INTERNATIONAL
      : BAEGUARD_RATE_PER_DVU_DOMESTIC
    return Math.ceil(declaredValue / 100) * ratePerDvu
  }, [
    isBaeGuardInsurance,
    formik.values?.insurance_option?.insured_amount,
    formik.values?.international,
  ])
  const insuranceOptions = useMemo(() => {
    const options = sortBy(
      addOnServices?.insurances?.map((item) => ({
        label: item.type.toUpperCase(),
        value: item.type,
      })) || [],
      ['label']
    )
    return [{label: 'NONE', value: null}, ...options]
  }, [addOnServices?.insurances])

  const handleSelectInsuranceOption = useCallback(
    ({value}) => {
      if (isNull(value)) {
        formik.setFieldValue('insurance_option.insured_amount', '0')
      }
      formik.setFieldValue('insurance_option.upic_terms_of_service', null)
      if (value === INSURANCE_TYPE.BAE_GUARD) {
        const uPicAccepted = Cookies.get(ACCEPT_TERM_UPIC_KEY)
        setIsAccept(uPicAccepted === 'true')
      } else {
        setIsAccept(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onChangeInsuredAmount = () => {
    if (!isBaeGuardInsurance) return
    if (isAccept && !(status === OrderStatus.SHIPPED && !isModal)) {
      if (!formik.values?.insurance_option?.upic_terms_of_service) {
        setIsErrorUpicAccept(true)
      }
    }
  }

  const renderTermUpic = useMemo(() => {
    if (!isBaeGuardInsurance) return null
    if (isAccept) {
      if (status === OrderStatus.SHIPPED && !isModal) {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: intl.formatMessage({id: 'UPIC_OPT_IN_CONDITION_ACCEPTED'}),
            }}
          />
        )
      }

      return (
        <div>
          <InputCheckboxFormik
            type='checkbox'
            label={
              <div
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({id: 'UPIC_OPT_TERM_AND_CONDITION'}),
                }}
              />
            }
            className='text-start align-items-start mt-0'
            labelClassName='fs-7 text-gray-700'
            name='insurance_option.upic_terms_of_service'
            formik={formik}
            onClick={(e) => {
              const checked = e.target.checked
              setIsErrorUpicAccept(!checked)
            }}
          />
          {isErrorUpicAccept && (
            <FormError
              className='ps-8'
              iconPath='/media/gori/orders/warning.svg'
              message={intl.formatMessage({id: 'YOU_MUST_ACCEPT_THE_TERMS_AND_CONDITIONS'})}
            />
          )}
        </div>
      )
    }

    return (
      <div
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage({id: 'UPIC_OPT_IN_CONDITION_ACCEPTED'}),
        }}
      />
    )
  }, [
    intl,
    isAccept,
    isBaeGuardInsurance,
    status,
    isModal,
    isErrorUpicAccept,
    setIsErrorUpicAccept,
    formik,
  ])

  return (
    <>
      <div className={clsx('d-flex flex-column gap-4 ', {'cursor-no-drop': disabled})}>
        <div className='bg-light p-6 rounded-2'>
          <div className={clsx('d-flex flex-column mb-4')}>
            <label className={`form-label fw-bolder`}>
              {intl.formatMessage({id: 'INSURANCE'})}
            </label>
            <SelectFormik
              className='col-12'
              options={insuranceOptions}
              onChange={handleSelectInsuranceOption}
              name='insurance_option.provider'
              emptyDefault={false}
              formik={formik}
              hasUseIntl
              disabled={disabled}
            />
          </div>
          <SeeDetailModal show={showSeeDetail} handleClose={() => setShowSeeDetail(false)} />
          {formik.values?.insurance_option?.provider && (
            <div>
              <div className='d-flex justify-content-between mb-4 flex-column flex-sm-row'>
                <div className={'text-start'}>
                  <label className={`form-label`}>
                    {intl.formatMessage({id: 'DECLARED_VALUE'})}
                  </label>
                  <InputTextFormik
                    preventWheelChange
                    required
                    className={clsx('w-140px')}
                    type='number'
                    name={'insurance_option.insured_amount'}
                    formik={formik}
                    min={0}
                    max={5000}
                    disabled={disabled}
                    placeholder={intl.formatMessage({id: 'AMOUNT'})}
                    configAppend={{
                      name: '$',
                      position: 'left',
                      classInput: 'ps-6',
                    }}
                    onChange={onChangeInsuredAmount}
                  >
                    {carriers[formik.values?.service]?.options?.insurance?.default_coverage > 0 && (
                      <span className='text-danger fs-7'>
                        {intl.formatMessage(
                          {id: 'INSURANCE_COVERAGE_TEXT'},
                          {
                            amount:
                              carriers[formik.values?.service]?.options?.insurance
                                ?.default_coverage,
                          }
                        )}
                      </span>
                    )}
                  </InputTextFormik>
                </div>
                {isBaeGuardInsurance && (
                  <div className='text-start text-sm-end mt-4 mt-sm-0'>
                    <div>{intl.formatMessage({id: 'INSURANCE_COST'})}</div>
                    <div className='fw-bold'>
                      {loadingRate ? (
                        <img
                          src={toAbsoluteUrl('/media/gori/orders/reload.gif')}
                          className='w-40px'
                          alt='reload-rate'
                        />
                      ) : (
                        <strong className='fs-2 ms-3 fw-bold'>
                          {formik.values?.insurance_option?.insured_fee
                            ? convertCurrency(formik.values?.insurance_option?.insured_fee)
                            : estimatedInsuranceFee
                            ? convertCurrency(estimatedInsuranceFee)
                            : '$0.00'}
                        </strong>
                      )}
                    </div>
                    <div className='text-gray-600 fs-7 d-flex '>
                      {intl.formatMessage({id: 'SERVICE_FEE_DVU'})}{' '}
                      <span
                        className='ms-1 align-items-center fw-bolder cursor-pointer btn-advanced-editorial'
                        onClick={() => {
                          setShowSeeDetail(true)
                        }}
                      >
                        {intl.formatMessage({id: 'SERVICE_FEE_DVU_SEE_DETAIL'})}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              {renderTermUpic}
            </div>
          )}
        </div>
        <div className='d-flex bg-light p-4 rounded-2'>
          <SelectFormik
            label={
              <div>
                {intl.formatMessage({id: 'SIGNATURE_COVERAGE'})}
                <div className='text-gray-600 fs-8 fw-normal'>
                  {intl.formatMessage({id: 'FEE_FROM_CHOSEN_CARRIER_APPLY'})}
                </div>
              </div>
            }
            className={clsx('col-6')}
            labelClassName='col-6 col-form-label fw-bolder'
            options={optionsSignature}
            name='signature_confirmation'
            emptyDefault={false}
            formik={formik}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  )
}
